import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import axios from 'axios';
import { LINKS } from '../constants/links';
import { apiUrl } from '../utils/apiConfig';
import { writeSchedulePrefs } from '../utils/schedulePrefs';
import { getToken } from '../utils/auth';
import { useMountTransition } from '../hooks/useMountTransition';
import { activateClose } from '../utils/dismissFocus';
import SmileLoader from './SmileLoader';
import './styles/Curatorship.css';

const isStudent = (user) => !user || user.role === 'student';
const PAGE_SIZE = 3;

const telHref = (phone) => {
  let digits = String(phone || '').replace(/\D/g, '');
  if (digits.length < 10) return '';
  if (digits.length === 11 && digits.startsWith('8')) {
    digits = `7${digits.slice(1)}`;
  } else if (digits.length === 10) {
    digits = `7${digits}`;
  }
  return `tel:+${digits}`;
};

const callPhoneOf = (student) => student?.phone || student?.parent_phone || '';

const startCall = (event, phone) => {
  event.preventDefault();
  event.stopPropagation();
  const href = telHref(phone);
  if (!href) return;
  const native = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.();
  if (native) {
    window.open(href, '_system');
    return;
  }
  window.location.href = href;
};

const authHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const peopleLabel = (n) => {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return `${n} студентов`;
  if (last === 1) return `${n} студент`;
  if (last >= 2 && last <= 4) return `${n} студента`;
  return `${n} студентов`;
};

const filenameFromHeader = (header, fallback) => {
  if (!header) return fallback;
  const star = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) {
    try {
      return decodeURIComponent(star[1]);
    } catch (err) {
      return fallback;
    }
  }
  const plain = header.match(/filename="([^"]+)"/i);
  return plain ? plain[1] : fallback;
};

const chunk = (list, size) => {
  const pages = [];
  for (let i = 0; i < list.length; i += size) {
    pages.push(list.slice(i, i + size));
  }
  return pages;
};

const ActionButton = ({ variant, icon, title, sub, onClick, disabled }) => (
  <button
    type="button"
    className={`curator-action curator-action--${variant}`}
    onClick={onClick}
    disabled={disabled}
  >
    <span className="curator-action__art" aria-hidden="true">
      <span className="curator-action__plate" />
      <span className="curator-action__icon">
        <i className={`bi ${icon}`}></i>
      </span>
    </span>
    <span className="curator-action__meta">
      <span className="curator-action__title">{title}</span>
      <span className="curator-action__sub">{sub}</span>
    </span>
  </button>
);

const StudentCard = ({ student, onDetails }) => {
  const phone = callPhoneOf(student);
  const canCall = Boolean(telHref(phone));
  return (
    <article className="curator-person">
      <span className="curator-person__ava" aria-hidden="true">
        {student.initial}
      </span>
      <div className="curator-person__body">
        <strong>{student.full_name}</strong>
        <div className="curator-person__btns">
          <button type="button" className="curator-person__btn" onClick={() => onDetails(student)}>
            Подробнее
          </button>
          <button
            type="button"
            className="curator-person__btn curator-person__btn--call"
            disabled={!canCall}
            onPointerDown={(event) => event.stopPropagation()}
            onTouchEnd={(event) => event.stopPropagation()}
            onClick={(event) => startCall(event, phone)}
          >
            Позвонить
          </button>
        </div>
      </div>
    </article>
  );
};

const Curatorship = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const { user, openExternal } = useOutletContext();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyKind, setBusyKind] = useState('');
  const [slide, setSlide] = useState(0);
  const [query, setQuery] = useState('');
  const [openStudent, setOpenStudent] = useState(null);
  const [shownStudent, setShownStudent] = useState(null);
  const touchRef = useRef(null);
  const { mounted: studentMounted, active: studentActive } = useMountTransition(
    Boolean(openStudent),
    320
  );

  useEffect(() => {
    if (openStudent) setShownStudent(openStudent);
  }, [openStudent]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setSlide(0);
    setQuery('');
    setOpenStudent(null);
  }, [groupId]);

  useEffect(() => {
    if (!studentMounted) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenStudent(null);
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('kkt-dialog-open');
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('kkt-dialog-open');
      document.body.style.overflow = prev;
    };
  }, [studentMounted]);

  useEffect(() => {
    if (isStudent(user)) return undefined;
    let cancelled = false;
    setLoading(true);
    axios
      .get(apiUrl('/api/profile/curatorship'), { headers: authHeaders() })
      .then((resp) => {
        if (cancelled) return;
        setGroups(resp.data?.groups || []);
        setError('');
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить группы');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const selected = useMemo(
    () => groups.find((g) => String(g.id) === String(groupId)) || null,
    [groups, groupId]
  );

  const filteredStudents = useMemo(() => {
    const list = selected?.students || [];
    const q = query.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!q) return list;
    return list.filter((student) =>
      String(student.full_name || '').toLowerCase().includes(q)
    );
  }, [selected, query]);

  const studentPages = useMemo(
    () => chunk(filteredStudents, PAGE_SIZE),
    [filteredStudents]
  );

  useEffect(() => {
    setSlide(0);
  }, [query, groupId]);

  useEffect(() => {
    if (loading || groupId || groups.length !== 1) return undefined;
    navigate(`/curatorship/${groups[0].id}`, { replace: true });
  }, [loading, groupId, groups, navigate]);

  if (isStudent(user)) {
    return <Navigate to="/payment" replace />;
  }

  const openSchedule = (group) => {
    writeSchedulePrefs({
      type: 'student',
      group: group.name,
      teacher: '',
    });
    navigate('/schedule');
  };

  const openInEo = (group) => {
    openExternal(`${LINKS.eo}/users/groups/${group.id}/`, 'электронное обучение');
  };

  const downloadBlank = async (group, kind, fallback) => {
    if (busyKind) return;
    setBusyKind(kind);
    setError('');
    try {
      const resp = await fetch(apiUrl(`/api/profile/curatorship/${group.id}/${kind}`), {
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error('download');
      const blob = await resp.blob();
      const name = filenameFromHeader(resp.headers.get('Content-Disposition'), fallback);
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
    } catch (err) {
      setError('Не удалось скачать файл. Попробуйте ещё раз.');
    } finally {
      setBusyKind('');
    }
  };

  const goToSlide = (index) => {
    setSlide(Math.max(0, Math.min(studentPages.length - 1, index)));
  };

  const onTouchStart = (event) => {
    const point = event.changedTouches?.[0];
    if (!point) return;
    touchRef.current = { x: point.clientX, y: point.clientY };
  };

  const onTouchEnd = (event) => {
    const start = touchRef.current;
    const point = event.changedTouches?.[0];
    touchRef.current = null;
    if (!start || !point) return;
    const dx = point.clientX - start.x;
    const dy = point.clientY - start.y;
    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
    goToSlide(slide + (dx < 0 ? 1 : -1));
  };

  if (loading) {
    return (
      <div className="curatorship-page">
        <section className="curatorship-panel curatorship-empty">
          <SmileLoader size="md" />
          <p>Загружаем группы…</p>
        </section>
      </div>
    );
  }

  if (groupId && !selected) {
    return (
      <div className="curatorship-page">
        <section className="curatorship-panel curatorship-empty">
          <h1>Группа не найдена</h1>
          <p>Её нет среди ваших групп.</p>
          <button type="button" className="curatorship-cta" onClick={() => navigate('/curatorship')}>
            К списку групп
          </button>
        </section>
      </div>
    );
  }

  if (selected) {
    return (
      <div className="curatorship-page curatorship-page--wide">
        {groups.length > 1 ? (
          <button type="button" className="curatorship-back" onClick={() => navigate('/curatorship')}>
            <i className="bi bi-chevron-left" aria-hidden="true"></i>
            Все группы
          </button>
        ) : null}
        <header className="curatorship-head">
          <div>
            <h1>{selected.name}</h1>
            <p className="curatorship-meta">
              {peopleLabel(selected.students_count)}
              {selected.specialty ? `, ${selected.specialty}` : ''}
              {selected.course ? `, ${selected.course}` : ''}
            </p>
          </div>
          {groups.length > 1 ? (
            <label className="curatorship-switch">
              <span>Группа</span>
              <select
                value={String(selected.id)}
                onChange={(event) => navigate(`/curatorship/${event.target.value}`)}
                aria-label="Переключить группу"
              >
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </header>

        {error ? <p className="curatorship-error">{error}</p> : null}

        <div className="curator-split">
          <div className="curator-actions" role="group" aria-label="Инструменты группы">
            <ActionButton
              variant="raport"
              icon="bi-clipboard2-check"
              title="Рапортичка группы"
              sub={busyKind === 'raportichka' ? 'Готовим файл…' : 'Скачать рапортичку'}
              onClick={() => downloadBlank(selected, 'raportichka', `Рапортичка_${selected.name}.docx`)}
              disabled={Boolean(busyKind)}
            />
            <ActionButton
              variant="journal"
              icon="bi-journal-text"
              title="Временный журнал"
              sub={busyKind === 'journal' ? 'Готовим файл…' : 'Скачать временный журнал'}
              onClick={() => downloadBlank(selected, 'journal', `Журнал_${selected.name}.xlsx`)}
              disabled={Boolean(busyKind)}
            />
            <ActionButton
              variant="sched"
              icon="bi-calendar3"
              title="Расписание"
              sub="Пары этой группы"
              onClick={() => openSchedule(selected)}
            />
            <ActionButton
              variant="eo"
              icon="bi-laptop"
              title="ЭО"
              sub="Карточка группы"
              onClick={() => openInEo(selected)}
            />
          </div>

          <section className="curatorship-panel curator-roster" aria-label="Студенты группы">
            <header className="curator-roster__head">
              <div>
                <h2>Студенты</h2>
                <p>
                  {query.trim()
                    ? `${peopleLabel(filteredStudents.length)} из ${selected.students_count}`
                    : peopleLabel(selected.students_count)}
                </p>
              </div>
            </header>
            <label className="curator-search">
              <i className="bi bi-search" aria-hidden="true"></i>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Найти по ФИО"
                aria-label="Поиск студента по ФИО"
              />
            </label>
            {studentPages.length ? (
              <>
                <div
                  className="curator-slider"
                  onTouchStart={onTouchStart}
                  onTouchEnd={onTouchEnd}
                >
                  <div className="curator-slider__page" key={`page-${slide}`}>
                    {(studentPages[slide] || []).map((student) => (
                      <StudentCard
                        key={student.id}
                        student={student}
                        onDetails={setOpenStudent}
                      />
                    ))}
                  </div>
                </div>
                {studentPages.length > 1 ? (
                  <div className="curator-slider__nav">
                    <button
                      type="button"
                      className="curator-slider__arrow"
                      onClick={() => goToSlide(slide - 1)}
                      disabled={slide <= 0}
                      aria-label="Предыдущие студенты"
                    >
                      <i className="bi bi-chevron-left" aria-hidden="true"></i>
                    </button>
                    <div className="curator-slider__dots">
                      {studentPages.map((_, index) => (
                        <button
                          type="button"
                          key={`dot-${index}`}
                          className={`curator-slider__dot${index === slide ? ' is-on' : ''}`}
                          onClick={() => goToSlide(index)}
                          aria-label={`Страница ${index + 1}`}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      className="curator-slider__arrow"
                      onClick={() => goToSlide(slide + 1)}
                      disabled={slide >= studentPages.length - 1}
                      aria-label="Следующие студенты"
                    >
                      <i className="bi bi-chevron-right" aria-hidden="true"></i>
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="curatorship-meta curator-roster__empty">
                {query.trim()
                  ? 'Никого не нашлось по этому ФИО.'
                  : 'В группе пока нет студентов.'}
              </p>
            )}
          </section>
        </div>
        {studentMounted && shownStudent && typeof document !== 'undefined'
          ? createPortal(
              <>
                <div
                  className={`kkt-blur-backdrop${studentActive ? ' is-visible' : ''}`}
                  onClick={() => setOpenStudent(null)}
                  role="presentation"
                />
                <div
                  className={`curator-sheet${studentActive ? ' is-visible' : ''}`}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="curator-student-title"
                >
                  <div className="curator-sheet__head">
                    <div className="curator-sheet__who">
                      <span className="curator-person__ava" aria-hidden="true">
                        {shownStudent.initial}
                      </span>
                      <h2 id="curator-student-title">{shownStudent.full_name}</h2>
                    </div>
                    <button
                      type="button"
                      className="curator-sheet__close"
                      onPointerDown={(event) => activateClose(event, () => setOpenStudent(null))}
                      onClick={(event) => activateClose(event, () => setOpenStudent(null))}
                      aria-label="Закрыть"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                  <dl className="curator-sheet__facts">
                    <div>
                      <dt>Телефон</dt>
                      <dd>
                        {shownStudent.phone || <span className="is-empty">не указан</span>}
                      </dd>
                    </div>
                    <div>
                      <dt>Дата рождения</dt>
                      <dd>
                        {shownStudent.birth_date || <span className="is-empty">не указана</span>}
                      </dd>
                    </div>
                    <div>
                      <dt>Родитель</dt>
                      <dd>
                        {shownStudent.parent_phone || <span className="is-empty">не указан</span>}
                      </dd>
                    </div>
                  </dl>
                </div>
              </>,
              document.body
            )
          : null}
      </div>
    );
  }

  if (!groups.length) {
    return (
      <div className="curatorship-page">
        <section className="curatorship-panel curatorship-empty">
          <span className="curatorship-empty__icon" aria-hidden="true">
            <i className="bi bi-people"></i>
          </span>
          <h1>Пока нет групп</h1>
          <p>
            Их назначает администратор ЭО. Когда группу привяжут к вам, она появится здесь —
            вместе с рапортичкой и журналом.
          </p>
          <button
            type="button"
            className="curatorship-cta curatorship-cta--ghost"
            onClick={() => openExternal(`${LINKS.eo}/users/groups/`, 'электронное обучение')}
          >
            Открыть группы в ЭО
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="curatorship-page">
      <section className="curatorship-panel">
        <header className="curatorship-head">
          <div>
            <h1>Мои группы</h1>
            <p className="curatorship-meta">
              {groups.length === 1 ? 'Одна группа' : `Групп: ${groups.length}`}
            </p>
          </div>
        </header>
        {error ? <p className="curatorship-error">{error}</p> : null}
        <ul className="curatorship-list">
          {groups.map((group) => (
            <li key={group.id}>
              <button
                type="button"
                className="curatorship-card"
                onClick={() => navigate(`/curatorship/${group.id}`)}
              >
                <span className="curatorship-card__title">{group.name}</span>
                <span className="curatorship-meta">
                  {peopleLabel(group.students_count)}
                  {group.specialty ? `, ${group.specialty}` : ''}
                </span>
                <i className="bi bi-chevron-right" aria-hidden="true"></i>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default Curatorship;
